import React, { Fragment, memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { MenuActionItem } from 'src/interfaces/ui'
import { CirclePlusMinor, CirclePlusOutlineMinor, ExportMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { FundingInvoice } from 'src/interfaces/payment'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { FundingInvoiceStatus, FundingType } from 'src/constants/payment/enums'
import { PaymentInProgressModal } from 'src/components/payment/add-payments/PaymentInProgressModal'

interface Props {
  currentInvoice: FundingInvoice | null
  onOpenBankFundInProgress(): void
  onOpenInstantFundInProgress(): void
  onOpenUploadMCPaymentsModal(): void
  onOpenAddSingleMCPaymentModal(): void
}

const AddMCPaymentsActionsButtonComponent = ({
  currentInvoice,
  onOpenBankFundInProgress,
  onOpenInstantFundInProgress,
  onOpenUploadMCPaymentsModal,
  onOpenAddSingleMCPaymentModal,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const {
    isOpen: isPaymentInProgressOpen,
    open: openPaymentInProgress,
    close: closePaymentInProgress,
  } = useModalOpen()

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (currentInvoice?.status === FundingInvoiceStatus.Pending) {
        openPaymentInProgress()

        return
      }

      setAnchorEl(event.currentTarget)
    },
    [currentInvoice, openPaymentInProgress],
  )

  const handleGoToDetails = useCallback(() => {
    closePaymentInProgress()

    if (currentInvoice?.type === FundingType.Wire) {
      onOpenBankFundInProgress()
    }

    if (currentInvoice?.type === FundingType.Loan) {
      onOpenInstantFundInProgress()
    }
  }, [currentInvoice?.type])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    return [
      {
        label: 'Upload CSV file',
        icon: <ExportMinor width={20} height={20} />,
        dataTestId: 'payments-mc-main-add-upload-csv',
        onClick: async () => {
          setAnchorEl(null)

          onOpenUploadMCPaymentsModal()
        },
      },
      {
        label: 'Add single settlement',
        icon: <CirclePlusOutlineMinor width={20} height={20} />,
        dataTestId: 'payments-mc-main-add-single-payment',
        onClick: async () => {
          setAnchorEl(null)

          onOpenAddSingleMCPaymentModal()
        },
      },
    ]
  }, [onOpenUploadMCPaymentsModal, onOpenAddSingleMCPaymentModal])

  if (!actionItems.length) {
    return null
  }

  return (
    <Fragment>
      <ActionButton
        data-testid="payments-mc-main-add-payments-menu-btn"
        selected={!!anchorEl}
        color="white"
        text="Add MC settlements"
        icon={<CirclePlusMinor width={20} height={20} />}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        itemClassName={tw`min-w-[210px]`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleCloseMenu}
      />
      <PaymentInProgressModal
        isOpen={isPaymentInProgressOpen}
        onClose={closePaymentInProgress}
        onGoToDetails={handleGoToDetails}
      />
    </Fragment>
  )
}

export const AddMCPaymentsActionsButton = memo(AddMCPaymentsActionsButtonComponent)
